import React, { useEffect, useRef, useState } from "react";
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  StyleSheet,
  TextInput,
  Animated,
  Easing,
  Linking,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import { useDevice, useScreenDimensions } from "../../_layout";
import { useToast } from "../../contexts/ToastContext";
import { useModalKeyboardShortcuts } from "../../platform/guards/modalKeyboardShortcuts";

const BlockViewRevertModal = ({ isVisible, onClose, onRevert, coach }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const [feedbackText, setFeedbackText] = useState("");
  const [isInputHovered, setIsInputHovered] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isCloseHovered, setIsCloseHovered] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const overlayAnimation = useRef(new Animated.Value(0)).current;
  const modalAnimation = useRef(new Animated.Value(0)).current;
  const contentAnimation = useRef(new Animated.Value(0)).current;
  const textInputRef = useRef(null);

  useEffect(() => {
    if (!isVisible) {
      overlayAnimation.setValue(0);
      modalAnimation.setValue(0);
      contentAnimation.setValue(0);
      setIsClosing(false);
      setFeedbackText("");
      return;
    }

    overlayAnimation.setValue(0);
    modalAnimation.setValue(0);
    contentAnimation.setValue(0);
    setIsClosing(false);

    Animated.sequence([
      Animated.timing(overlayAnimation, {
        toValue: 1,
        duration: 220,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(modalAnimation, {
        toValue: 1,
        duration: 320,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(contentAnimation, {
        toValue: 1,
        duration: 240,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start(() => {
      // Focus the input after animations complete
      if (textInputRef.current) {
        setTimeout(() => {
          textInputRef.current?.focus();
        }, 50);
      }
    });
  }, [contentAnimation, isVisible, modalAnimation, overlayAnimation]);

  const handleRevert = async () => {
    const trimmedFeedback = feedbackText.trim();
    setFeedbackText("");
    if (trimmedFeedback && coach?.uploadFeedbackFirestore) {
      try {
        await coach.uploadFeedbackFirestore(trimmedFeedback, "revert_modal");
        showToast("feedbackThanksTitle-string", {
          type: "info",
          description: "feedbackThanksDescription-string",
        });
      } catch (error) {
        console.error("Error uploading feedback:", error);
      }
    }
    if (onRevert) {
      await onRevert(trimmedFeedback);
    }
    setFeedbackText("");
  };

  const handleClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    Animated.parallel([
      Animated.timing(contentAnimation, {
        toValue: 0,
        duration: 160,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(modalAnimation, {
        toValue: 0,
        duration: 220,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(overlayAnimation, {
        toValue: 0,
        duration: 220,
        easing: Easing.in(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start(({ finished }) => {
      if (finished) {
        onClose?.();
      }
    });
  };

  const overlayAnimatedStyle = {
    opacity: overlayAnimation,
  };

  const modalAnimatedStyle = {
    opacity: modalAnimation,
    transform: [
      {
        translateY: modalAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [14, 0],
        }),
      },
      {
        scale: modalAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [0.98, 1],
        }),
      },
    ],
  };

  const contentAnimatedStyle = {
    opacity: contentAnimation,
    transform: [
      {
        translateY: contentAnimation.interpolate({
          inputRange: [0, 1],
          outputRange: [8, 0],
        }),
      },
    ],
  };

  useModalKeyboardShortcuts({
    isVisible,
    onConfirm: handleRevert,
    onCancel: handleClose,
    isConfirmEnabled: !isClosing,
    isBlocked: isClosing,
  });

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isVisible}
      onRequestClose={handleClose}
    >
      <View style={GlobalStyles.centeredView}>
        <Animated.View
          style={[styles.backdrop, overlayAnimatedStyle]}
          pointerEvents="box-none"
        >
          <TouchableOpacity
            onPress={handleClose}
            style={styles.backdropTouchable}
          />
        </Animated.View>
        <Animated.View
          style={[
            styles.modalView,
            modalAnimatedStyle,
            { maxWidth: screenSize !== "small" ? 420 : "90%" },
          ]}
        >
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
            onMouseLeave={() => setIsCloseHovered(false)}
          >
            <Ionicons
              name="close"
              size={22}
              color={isCloseHovered ? Colors.darkgrayColor : Colors.grayColor}
            />
          </TouchableOpacity>
          <Animated.View style={contentAnimatedStyle}>
            <Text style={[Fonts.bold18, styles.title]}>
              {t("blockViewRevertTitle-string")}
            </Text>
            <View style={styles.subTextContainer}>
              {(() => {
                const subtitleText = t("blockViewRevertSubtitle-string");
                const instagramHandle = "@efortapp";
                const instagramUrl = "https://www.instagram.com/efortapp/";
                const parts = subtitleText.split(instagramHandle);

                if (parts.length === 2) {
                  return (
                    <Text style={styles.subText}>
                      {parts[0]}
                      <Text
                        style={styles.instagramLink}
                        onPress={() => Linking.openURL(instagramUrl)}
                      >
                        {instagramHandle}
                      </Text>
                      {parts[1]}
                    </Text>
                  );
                }
                return <Text style={styles.subText}>{subtitleText}</Text>;
              })()}
            </View>
          </Animated.View>
          <Animated.View style={[contentAnimatedStyle, styles.inputWrapper]}>
            <TextInput
              ref={textInputRef}
              value={feedbackText}
              onChangeText={setFeedbackText}
              multiline
              numberOfLines={6}
              placeholder={t("blockViewRevertPlaceholder-string")}
              placeholderTextColor={Colors.grayColor}
              style={[
                styles.textInput,
                isInputHovered && styles.textInputHovered,
                isInputFocused && styles.textInputFocused,
              ]}
              maxLength={4000}
              selectionColor={Colors.bubbleColor}
              textAlignVertical="top"
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              onMouseEnter={() => !isMobile && setIsInputHovered(true)}
              onMouseLeave={() => setIsInputHovered(false)}
            />
          </Animated.View>
          <View style={styles.actionsRow}>
            <TouchableOpacity
              style={[
                styles.primaryButton,
                isButtonHovered && styles.primaryButtonHovered,
              ]}
              onPress={handleRevert}
              onMouseEnter={() => !isMobile && setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
            >
              <Text style={styles.primaryText}>
                {t("blockViewRevertButton-string")}
              </Text>
            </TouchableOpacity>
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalView: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 18,
    padding: 24,
    width: "auto",
    alignSelf: "center",
    gap: 12,
    zIndex: 1000,
    elevation: 8,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.18,
    shadowRadius: 8,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(15, 15, 15, 0.25)",
  },
  backdropTouchable: {
    ...StyleSheet.absoluteFillObject,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 10,
    cursor: "pointer",
  },
  title: {
    marginRight: 32,
    marginBottom: 12,
    color: Colors.blackColor,
  },
  subTextContainer: {
    marginBottom: 16,
  },
  inputWrapper: {
    minHeight: 120,
    marginTop: -12,
  },
  subText: {
    fontSize: 13,
    color: Colors.darkgrayColor,
    lineHeight: 18,
  },
  instagramLink: {
    color: Colors.bubbleColor,
    textDecorationLine: "underline",
    cursor: "pointer",
  },
  inputWrapper: {
    minHeight: 120,
    marginTop: -12,
  },
  textInput: {
    width: "100%",
    height: 120,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: "transparent",
    paddingVertical: 12,
    paddingHorizontal: 12,
    outline: "none",
    fontSize: 14,
    color: Colors.blackColor,
    lineHeight: 18,
  },
  textInputHovered: {
    borderColor: Colors.mediumGray,
  },
  textInputFocused: {
    borderColor: Colors.bubbleColor,
  },
  actionsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 12,
    minHeight: 36,
  },
  primaryButton: {
    height: 36,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    backgroundColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryText: {
    fontSize: 14,
    fontWeight: "600",
    color: Colors.whiteColor,
  },
});

export default BlockViewRevertModal;
